import { GqlServiceV2 } from '../modules/content/service/gql.service-v2';
import { Injectable } from '@angular/core';
import { IdInput } from '@libs/types/src/common/input-type/id.input';
import {
    FolderIndividualPlanCreateInput,
} from '@libs/types/src/services/input-type/folder/folder-individual-plan.create-input';
import {
    FolderIndividualPlanUpdateInput,
} from '@libs/types/src/services/input-type/folder/folder-individual-plan.update-input';
import { GridInput } from '@it2go/data-grid';
import { filterFrag } from './types/filter.types';
import {
    FolderIndividualPlanGridObject,
    FolderIndividualPlanObject,
} from '@libs/types/src/services/object-type/folder/folder-individual-plan.object';
import { OkObject } from '@it2go/types';
import {
    FolderIndividualPlanTagGridObject,
} from '@libs/types/src/services/object-type/folder/folder-individual-plan-tag.object';
import {
    FolderIndividualPlanTargetGridObject,
} from '@libs/types/src/services/object-type/folder/folder-individual-plan-target.object';
import { SearchIdInput } from '@libs/types/src/common/input-type/search-id.input';
import {
    FolderIndividualPlanPrintInput,
} from '@libs/types/src/services/input-type/folder/folder-individual-plan.print-input';
import {
    FolderIndividualPlanRevisionsInput,
} from '@libs/types/src/services/input-type/folder/folder-individual-plan.revisions-input';
import {
    FolderIndividualPlanOfferGridObject
} from '@libs/types/src/services/object-type/folder/folder-individual-plan-offer.object';

@Injectable({
    providedIn: 'root',
})
export class FolderIndividualPlanGqlService extends GqlServiceV2 {

    public async getIndividualPlans(input: GridInput, fragment: string) {
        return this.query<FolderIndividualPlanGridObject>(
            `
                query($input: GridInput!) {
                    folderIndividualPlan {
                        filter (input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getIndividualPlanRevisions(input: FolderIndividualPlanRevisionsInput[], fragment: string) {
        return this.query<FolderIndividualPlanObject[]>(
            `
                query($input: [FolderIndividualPlanRevisionsInput!]!) {
                    folderIndividualPlan {
                        revisions (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getIndividualPlanDashboardRevisions(input: FolderIndividualPlanRevisionsInput[], fragment: string) {
        return this.query<FolderIndividualPlanObject[]>(
            `
                query($input: [FolderIndividualPlanRevisionsInput!]!) {
                    folderIndividualPlan {
                        dashboardRevisions (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getIndividualPlanOffers(input: GridInput, fragment: string) {
        return this.query<FolderIndividualPlanOfferGridObject>(
            `
                query($input: GridInput!) {
                    folderIndividualPlanOffer {
                        filter (input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getIndividualPlanTags(input: GridInput, fragment: string) {
        return this.query<FolderIndividualPlanTagGridObject>(
            `
                query($input: GridInput!) {
                    folderIndividualPlanTag {
                        filter (input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getIndividualPlanTargets(input: GridInput, fragment: string) {
        return this.query<FolderIndividualPlanTargetGridObject>(
            `
                query($input: GridInput!) {
                    folderIndividualPlanTarget {
                        filter (input: $input) {
                            ${filterFrag}
                            items {
                                ${fragment}
                            }
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async getIndividualPlanTargetNamesByFolder(input: SearchIdInput) {
        return this.query<string[]>(
            `
                query($input: SearchIdInput!) {
                    folderIndividualPlanTarget {
                        serviceTargetNamesByFolder (input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public async getIndividualPlanServiceTags(input: IdInput) {
        return this.query<string[]>(
            `
                query($input: IdInput!) {
                    folderIndividualPlanTag {
                        serviceTags (input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public async getIndividualPlanFolderTags(input: IdInput) {
        return this.query<string[]>(
            `
                query($input: IdInput!) {
                    folderIndividualPlanTag {
                        folderTags (input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public async getIndividualPlanServiceTagsByFolder(input: IdInput) {
        return this.query<string[]>(
            `
                query($input: IdInput!) {
                    folderIndividualPlanTag {
                        serviceTagsByFolder (input: $input)
                    }
                }
            `,
            { input },
        );
    }

    public async createIndividualPlan(input: FolderIndividualPlanCreateInput, fragment: string) {
        return this.mutate<FolderIndividualPlanObject>(
            `
                mutation($input: FolderIndividualPlanCreateInput!) {
                    folderIndividualPlan {
                        create (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async updateIndividualPlan(input: FolderIndividualPlanUpdateInput, fragment: string) {
        return this.mutate<FolderIndividualPlanObject>(
            `
                mutation($input: FolderIndividualPlanUpdateInput!) {
                    folderIndividualPlan {
                        update (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async deleteIndividualPlan(input: IdInput, fragment: string = 'ok') {
        return this.mutate<OkObject>(
            `
                mutation($input: IdInput!) {
                    folderIndividualPlan {
                        delete (input: $input) {
                            ${fragment}
                        }
                    }
                }
            `,
            { input },
        );
    }

    public async printIndividualPlan(input: FolderIndividualPlanPrintInput) {
        return this.mutate<OkObject>(
            `
                mutation($input: FolderIndividualPlanPrintInput!) {
                    folderIndividualPlan {
                        print (input: $input)
                    }
                }
            `,
            { input },
        );
    }

}
